/** Surface feed tabs — each maps to a `verification_status` feed filter. */
export type SurfaceTab = "review" | "skipped" | "accepted" | "rejected";

/** Statuses accepted by `?verification_status=` on the feed. */
export type SurfaceFeedVerificationStatus =
  | "pending_review"
  | "skipped"
  | "accepted"
  | "rejected";

/** Server verification statuses for surface assets. */
export type VerificationStatus =
  | SurfaceFeedVerificationStatus
  | "awaiting_suggestion";

export type SurfaceWriteAction = "accept" | "reject" | "skip" | "reprocess";

export interface SurfaceProgress {
  pending_review: number;
  max_pending_review: number;
  pending_quota_remaining: number;
  awaiting_suggestion: number;
  accepted?: number;
  rejected?: number;
  skipped?: number;
}

export interface PromoteSurfaceAsset {
  id: number;
  verification_status: VerificationStatus;
  caption?: string | null;
  alt_text?: string | null;
  url?: string | null;
  thumbnail_url?: string | null;
  ai_observations?: string[];
  /** Tag ids currently on the asset (solution + non-solution). */
  tag_ids?: number[];
  /** Alternate key some payloads may use instead of `tag_ids`. */
  tags?: number[];
  actioned_by?: number | null;
  actioned_by_name?: string | null;
  actioned_at?: string | null;
  created_at?: string | null;
  is_restricted?: boolean;
  restriction_reason?: string | null;
  visibility_warnings?: string[];
}

export interface SurfaceFeedResponse {
  assets: PromoteSurfaceAsset[];
  next_cursor: number | string | null;
  progress?: SurfaceProgress;
}

export interface SurfaceSolutionTag {
  id: number;
  /** MarketingTag.label from `/surface/solution-tags`. */
  label: string;
  is_solution?: boolean;
  type?: string;
  tag_group?: string | null;
  description?: string | null;
}

export interface AcceptSurfaceAssetBody {
  tag_ids: number[];
  caption?: string | null;
  alt_text?: string | null;
  is_restricted?: boolean;
  restriction_reason?: string | null;
}

export interface SurfaceBulkBody {
  action: SurfaceWriteAction;
  asset_ids: number[];
  /** Required when action is accept. */
  tag_ids?: number[];
  caption?: string | null;
  alt_text?: string | null;
  is_restricted?: boolean;
  restriction_reason?: string | null;
}

export interface SurfaceBulkResultItem {
  asset_id: number;
  ok: boolean;
  status?: number;
  error?: string;
  asset?: PromoteSurfaceAsset;
}

export interface SurfaceBulkResponse {
  results?: SurfaceBulkResultItem[];
  progress?: SurfaceProgress;
}

export interface FetchSurfaceFeedParams {
  /**
   * Preferred filter (takes precedence over legacy `tab` on the server).
   * Repeatable: pass an array for OR.
   */
  verificationStatus: SurfaceFeedVerificationStatus | SurfaceFeedVerificationStatus[];
  limit?: number;
  cursor?: number | string | null;
}

/** Map UI tab → feed `verification_status` filter. */
export function verificationStatusForTab(tab: SurfaceTab): SurfaceFeedVerificationStatus {
  if (tab === "review") return "pending_review";
  if (tab === "skipped") return "skipped";
  return tab;
}
